// @flow
/**
 * Board identity for the admin Scheduler and Master schedule.
 * A board is program + date + schedule type. Master dates are 1900-01-01..07.
 * Coach fetches leave intendedBoard null and are not gated.
 */

export type BoardSelection = {
  programId: ?number,
  date: string,
  scheduleTypeId: ?number,
  scheduleId?: ?number,
  isMaster?: boolean,
};

export type CapturedBoard = {
  epoch: number,
  date: ?string,
  programId: ?number,
  scheduleTypeId: ?number,
};

const inflight = new Map();

export function positiveId(raw: mixed): ?number {
  if (raw == null || raw === '') {
    return null;
  }
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return n > 0 ? n : null;
}

export function isMasterDate(date: ?string): boolean {
  return /^1900-01-0[1-7]$/.test(String(date || ''));
}

export function boardFlightKey(
  epoch: number,
  programId: ?number,
  date: ?string,
  scheduleTypeId: ?number
): string {
  return `${epoch}|${programId}|${date}|${scheduleTypeId}`;
}

/**
 * One in-flight promise per key. A second call returns the same promise.
 */
export function singleFlight(
  key: string,
  factory: () => Promise<any>
): Promise<any> {
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }
  let promise;
  try {
    promise = Promise.resolve(factory());
  } catch (err) {
    promise = Promise.reject(err);
  }
  const tracked = promise.finally(() => {
    if (inflight.get(key) === tracked) {
      inflight.delete(key);
    }
  });
  inflight.set(key, tracked);
  return tracked;
}

export function shouldApplyBoardResult(
  state: { intendedBoard: ?BoardSelection, boardEpoch: number },
  captured: ?CapturedBoard
): boolean {
  // Coach fetches pass no capture. A captured admin fetch must not paint
  // after logout or Scheduler unmount has cleared intendedBoard.
  if (!state || !state.intendedBoard) {
    return captured == null;
  }
  if (!captured || state.boardEpoch !== captured.epoch) {
    return false;
  }
  const board = state.intendedBoard;
  if (String(board.date) !== String(captured.date)) {
    return false;
  }
  if (
    board.programId != null &&
    captured.programId != null &&
    Number(board.programId) !== Number(captured.programId)
  ) {
    return false;
  }
  if (
    board.scheduleTypeId != null &&
    captured.scheduleTypeId != null &&
    Number(board.scheduleTypeId) !== Number(captured.scheduleTypeId)
  ) {
    return false;
  }
  return true;
}

/**
 * A schedule.delta applies only when date, program, and schedule type match
 * the open board. scheduleId is compared only after this board's getSuccess
 * has stored an id (null during load must not reject the new row).
 */
export function deltaTargetsOpenBoard(
  msg: ?Object,
  board: ?BoardSelection
): boolean {
  if (!msg || !board) {
    return false;
  }
  if (msg.date == null || msg.scheduleTypeId == null || msg.programId == null) {
    return false;
  }
  if (String(msg.date) !== String(board.date)) {
    return false;
  }
  if (Number(msg.scheduleTypeId) !== Number(board.scheduleTypeId)) {
    return false;
  }
  if (
    board.programId != null &&
    Number(msg.programId) !== Number(board.programId)
  ) {
    return false;
  }
  if (
    msg.scheduleId != null &&
    board.scheduleId != null &&
    Number(msg.scheduleId) !== Number(board.scheduleId)
  ) {
    return false;
  }
  return true;
}
